import { workspaceFetch } from '../../../../shared/platform/business-workspaces';
/** Local synthetic, read-only slice authorized by P2-W09-SYNC-FB01-IMPL. */
import { configuredTransportOrigin } from '../../../../shared/config/api-origin';
import type { CapabilityDefinitionReadPort } from './capability-definition-port';
import { parseCapabilityDefinitionWire } from './capability-definition-wire';

interface LiveCapabilityDefinitionOptions {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}

function localOrigin(baseOrigin: string): string {
  const configured = configuredTransportOrigin(baseOrigin);
  if (configured) return configured;
  // Check the supplied path before URL normalization can erase dot segments.
  if (!/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(baseOrigin)) {
    throw new Error('A root local HTTP origin is required');
  }
  const url = new URL(baseOrigin);
  const ipv4 = url.hostname.split('.');
  const loopback =
    url.hostname === 'localhost' ||
    url.hostname === '[::1]' ||
    (ipv4.length === 4 &&
      ipv4[0] === '127' &&
      ipv4.every((part) => /^\d+$/.test(part) && Number(part) <= 255));
  if (!loopback || url.username || url.password || url.search || url.hash || url.pathname !== '/') {
    throw new Error('A root local HTTP origin is required');
  }
  return url.origin;
}

export function createLiveCapabilityDefinitionAdapter({
  baseOrigin,
  fetchImpl,
}: LiveCapabilityDefinitionOptions): CapabilityDefinitionReadPort {
  const origin = localOrigin(baseOrigin);
  return {
    async read(query) {
      const id = query.capabilityId;
      if (id === '.' || id === '..') throw new Error('Capability identity cannot be a dot segment');
      const url = `${origin}/read-models/capability-definitions/${encodeURIComponent(id)}`;
      const response = await (fetchImpl ?? workspaceFetch)(url, {
        method: 'GET',
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
      });
      if (response.status !== 200) throw new Error('Capability definition HTTP request failed');
      const mediaType = response.headers.get('Content-Type')?.split(';')[0]?.trim().toLowerCase();
      if (mediaType !== 'application/json') throw new Error('Capability definition JSON required');
      const value: unknown = await response.json();
      return parseCapabilityDefinitionWire(value, id);
    },
  };
}
